import { Button } from '@travelnest/merlin.button';
import { InputStepper } from '@travelnest/merlin.input-stepper';
import { Modal } from '@travelnest/merlin.modal';
import theme from '@travelnest/merlin.theme';
import React, { useState } from 'react';
import {
	calculateMaxDeferralHours,
	getArrivalDateTime,
} from '../helpers/helpers';

interface IExtendPaymentRemindersModalProps {
	onRequestClose: () => any;
	extendPaymentReminders: (extension: number) => any;
	cancellationDate: string;
	startOn: string;
	checkInTime: string;
}

const processDate = (
	dateString: string | null,
	processor?: (date: Date) => void
): string => {
	if (!dateString) return 'Not available';

	try {
		const date = new Date(dateString);
		if (processor) processor(date);

		const dateOptions: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		};

		const timeOptions: Intl.DateTimeFormatOptions = {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		};

		const formattedDate = date.toLocaleDateString('en-GB', dateOptions);
		const formattedTime = date.toLocaleTimeString('en-GB', timeOptions);

		return `${formattedDate} ${formattedTime}`;
	} catch (error) {
		console.error('Error processing date:', error);
		return 'Invalid date';
	}
};

const formatDateToLocal = (dateString: string | null): string => {
	return processDate(dateString);
};

const addHoursToDate = (currentDateTime: string, hours: number): string => {
	return processDate(currentDateTime, (date) => {
		date.setHours(date.getHours() + hours);
	});
};

export const ExtendPaymentRemindersModal: React.FC<
	IExtendPaymentRemindersModalProps
> = ({
	onRequestClose,
	extendPaymentReminders,
	cancellationDate,
	startOn,
	checkInTime,
}) => {
	const [deferralHours, setDeferralHours] = useState(0);
	const [isExtendingPaymentReminders, setIsExtendingPaymentReminders] =
		useState(false);

	const arrivalDateTime = getArrivalDateTime(startOn, checkInTime);
	const maxDeferralHours = calculateMaxDeferralHours(
		startOn,
		checkInTime,
		cancellationDate
	);

	const onConfirm = async () => {
		if (deferralHours <= 0) {
			return;
		}

		setIsExtendingPaymentReminders(true);

		try {
			await extendPaymentReminders(deferralHours);
		} catch (error) {
			console.error('Failed to extend payment reminders:', error);
		} finally {
			setIsExtendingPaymentReminders(false);
			onRequestClose();
		}
	};

	return (
		<Modal
			title="Extend Payment Reminders"
			shouldCloseOnOverlayClick={true}
			onRequestClose={onRequestClose}
			actionRHS={
				<Button
					loading={isExtendingPaymentReminders}
					id="cancel_payment_reminders_confirm"
					onClick={onConfirm}
					bgColor={theme.colours.success.default}
					color={theme.colours.text.light}
					disabled={deferralHours <= 0}
				>
					Confirm
				</Button>
			}
		>
			<div className="booking_refresh_content">
				<p>
					Are you sure you want to extend the payment reminders for
					this booking? This will update any payment reminders not
					already sent to be sent out at a later date.
				</p>
				<InputStepper
					id="payment-link-extender"
					onClick={(value) => setDeferralHours(value)}
					minValue={0}
					step={12}
					maxValue={maxDeferralHours}
					value={deferralHours}
					valueFormatter={(n: number) =>
						`${n} hour${n === 1 ? '' : 's'}`
					}
				/>
				<p>
					{deferralHours > 0 ? (
						<>
							The booking will now be cancelled on
							<span style={{ textDecoration: 'line-through' }}>
								{` ${formatDateToLocal(cancellationDate)} `}
							</span>
							{` → `}
							{` ${addHoursToDate(cancellationDate, deferralHours)} `}
							if payment is not made.
						</>
					) : (
						<>
							The booking is set to be cancelled on
							{` ${formatDateToLocal(cancellationDate)} `}
							if payment is not made.
						</>
					)}
					<br />
					{maxDeferralHours === 0 && (
						<>
							<br />
							<strong>
								Note: Payment reminders cannot be extended as we
								are within 48 hours of the arrival date (
								{formatDateToLocal(arrivalDateTime)}).
							</strong>
						</>
					)}
					{maxDeferralHours > 0 && (
						<>
							<br />
							<strong>
								Note: Payment can be deferred up to{' '}
								{maxDeferralHours} hours to ensure payment is
								made at least 48 hours before arrival (
								{formatDateToLocal(arrivalDateTime)}).
							</strong>
						</>
					)}
				</p>
			</div>
		</Modal>
	);
};
